import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, MapPin, Globe, Calendar } from "lucide-react";
import api from "../lib/api";
import { Spinner, Pill, SectionLabel } from "../components/Primitives";
import BookmarkButton from "../components/BookmarkButton";
import ShareButton from "../components/ShareButton";
import usePageMeta from "../hooks/usePageMeta";
import AttachedFiles from "../components/AttachedFiles";

export default function KingdomDetail() {
  const { slug } = useParams();
  const [k, setK] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/kingdoms/${slug}`).then((r) => setK(r.data)).catch(() => setK(null)).finally(() => setLoading(false));
  }, [slug]);

  usePageMeta(
    k
      ? { title: k.name, description: k.summary, image: k.image_url }
      : { title: "Kingdoms" }
  );

  if (loading) return <div className="min-h-[60vh]"><Spinner /></div>;
  if (!k) return <div className="mx-auto max-w-7xl px-6 py-32 text-center text-muted">Kingdom not found.</div>;

  return (
    <div data-testid="kingdom-detail-page">
      <div className="relative h-[46vh] min-h-[320px] overflow-hidden">
        <img src={k.image_url} alt={k.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/70 to-ink/30" />
        <div className="absolute inset-x-0 bottom-0">
          <div className="mx-auto max-w-5xl px-6 pb-10 md:px-12">
            <Link to="/kingdoms" className="mb-4 inline-flex items-center gap-2 text-sm text-gold hover:underline" data-testid="back-to-kingdoms">
              <ArrowLeft className="h-4 w-4" /> All Kingdoms
            </Link>
            <div className="flex items-center gap-2">
              <Pill tone="gold">{k.type}</Pill>
              {k.ai_generated && <Pill tone="blood">AI Generated</Pill>}
            </div>
            <h1 className="mt-3 font-serif text-5xl font-semibold text-bone lg:text-6xl">{k.name}</h1>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-5xl px-6 py-14 md:px-12">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-edge pb-8">
          <div className="flex flex-wrap gap-8">
            <Meta icon={Calendar} label="Era" value={k.era} />
            <Meta icon={MapPin} label="Region" value={k.region} />
            <Meta icon={Globe} label="Modern Nations" value={k.modern_countries} />
          </div>
          <div className="flex items-center gap-2">
            <BookmarkButton itemType="kingdom" itemId={k.slug} title={k.name} />
            <ShareButton title={k.name} text={`${k.name} — ${k.summary}`} />
          </div>
        </div>

        <p className="mt-10 font-serif text-2xl leading-relaxed text-[#e8e3d8]">{k.summary}</p>

        <div className="mt-12">
          <SectionLabel>Culture & Society</SectionLabel>
          <p className="leading-[1.9] text-[#cfc9bd]">{k.culture}</p>
        </div>

        <div className="mt-12">
          <SectionLabel>Contributions</SectionLabel>
          <ul className="grid gap-3 sm:grid-cols-2">
            {(k.contributions || []).map((c, i) => (
              <li key={i} className="flex gap-3 border border-edge bg-ash p-4 text-sm text-[#cfc9bd]">
                <span className="font-mono text-gold">{String(i + 1).padStart(2, "0")}</span>
                {c}
              </li>
            ))}
          </ul>
        </div>

        <div className="mt-12">
          <SectionLabel>Interactions & Trade</SectionLabel>
          <p className="leading-[1.9] text-[#cfc9bd]">{k.interactions}</p>
        </div>

        <AttachedFiles attachType="kingdom" slug={k.slug} />
      </div>
    </div>
  );
}

function Meta({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.18em] text-gold">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-sm text-bone">{value || "—"}</div>
    </div>
  );
}
